import React from 'react';
import PropTypes from 'prop-types';
import Styled from 'styled-components';
import Theme from '../common/NekoTheme';
import {
  Column,
  useTable,
  useRowSelect
} from 'react-table'
import { Icon } from '@iconify/react';
import chevronDown from '@iconify/icons-mdi/chevron-down';
import checkBoxOutline from '@iconify/icons-mdi/check-box-outline';
import checkboxBlankOutline from '@iconify/icons-mdi/checkbox-blank-outline';
import checkBold from '@iconify/icons-mdi/check-bold';

const Table = Styled.table`
  background-color: ${Theme.white};
  font-family: Lato;
  border-spacing: 0;
  border: 1px solid rgba(${Theme.blackRGB}, 0.1);
  width: 100%;

  th,
  tfoot td {
    background-color: ${Theme.blue};
    color: ${Theme.white};
    font-style: normal;
    font-weight: normal;
    font-size: 13px;
    line-height: 16px;
    text-align: left;
  }

  th,
  td {
    margin: 0;
    padding: 0.5rem;
    border-bottom: 1px solid rgba(${Theme.blackRGB}, 0.1);
    border-right: 1px solid rgba(${Theme.blackRGB}, 0.1);

    :last-child {
      border-right: 0;
    }
  }

  thead {
    th {
      div {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      :first-child {
        width: 35px;
        box-sizing: border-box;
      }
    }
  }

  tbody {
    tr {
      :nth-child(even) {
        background-color: ${Theme.tableGray};
      }
    }
  }

  tfoot {
    tr {
      td {
        padding: 12.5px 8px;
      }
      :last-child {
        td {
          border-bottom: 0;
        }
      }
    }
  }
`;

const CheckboxContainer = Styled.div`
  input {
    display: none;
  }
`;

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, checked, ...rest }, ref) => {
    const defaultRef = React.useRef()
    const resolvedRef = ref || defaultRef
    const [isChecked, check] = React.useState(checked);

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate
    }, [resolvedRef, indeterminate])

    const onClick = () => {
      check(!isChecked);
    }

    return (
      <CheckboxContainer>
        <input type="checkbox" ref={resolvedRef} checked={isChecked} {...rest} />
        <Icon icon={isChecked ? checkBoxOutline : checkboxBlankOutline} width="20px" height="20px" onClick={onClick} />
      </CheckboxContainer>
    )
  }
)

/**
 * @param {string} className
 * @param {string|object} icon
 * @param {bool} disabled
 * @param {function} onClick
 */
const StyledNekoTable = (props) => {

  const {columns, data} = props;

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    rows,
    prepareRow,
    selectedFlatRows,
    state: { selectedRowIds },
  } = useTable(
    {
      columns,
      data
    },
    useRowSelect,
    hooks => {
      hooks.allColumns.push(columns => {
        const existedColumns = columns.map(column => {
          return {
            ...column,
            Header: <div>{column.Header}{column.Header ? <Icon icon={chevronDown} color={Theme.white} width="1.5rem" height="1.5rem" />: null}</div>
          };
        })

        return [
          {
            id: 'selection',
            // The header can use the table's getToggleAllRowsSelectedProps method
            // to render a checkbox
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <div>
                <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
              </div>
            ),
            // The cell can use the individual row's getToggleRowSelectedProps method
            // to the render a checkbox
            Cell: ({ row }) => (
              <div>
                <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
              </div>
            ),
          },
          ...existedColumns
        ]
      })
    }
  )

  return (
    <Table {...getTableProps()}>
      <thead>
        {headerGroups.map(headerGroup => (
          <tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map(column => (
              <th {...column.getHeaderProps()}>{column.render('Header')}</th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.map(row => {
          prepareRow(row)
          return (
            <tr {...row.getRowProps()}>
              {row.cells.map(cell => {
                return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
              })}
            </tr>
          )
        })}
      </tbody>
      <tfoot>
      {footerGroups.map(group => (
          <tr {...group.getFooterGroupProps()}>
            {group.headers.map(column => (
              <td {...column.getFooterProps()}>{column.render('Footer')}</td>
            ))}
          </tr>
        ))}
      </tfoot>
    </Table>
  );
};

const NekoTable = (props) => {
  return (<StyledNekoTable {...props} />);
}

NekoTable.propTypes = {
  columns: PropTypes.arrayOf(Column),
  data: PropTypes.arrayOf(PropTypes.any)
};

NekoTable.defaultProps = {
  columns: undefined,
  data: undefined
};

export { NekoTable };
